import { memo, useCallback, useMemo } from "react";

import { PaginationView } from "@react/components/Pagination/Pagination.view";
import { ThemeMode } from "@root/tablescape/state";

type Props = {
  handlePageChange: (page: number) => void;
  itemsPerPage: number;
  selectedPage: number;
  theme?: ThemeMode;
  totalItems: number;
};

const PaginationBase = ({
  handlePageChange,
  itemsPerPage,
  selectedPage,
  theme = ThemeMode.LIGHT,
  totalItems,
}: Props) => {
  const lastPage = useMemo(
    () => Math.ceil(totalItems / itemsPerPage),
    [itemsPerPage, totalItems]
  );

  const resolvedSelectedPage = useMemo(
    () => Math.min(Math.max(1, selectedPage), lastPage),
    [selectedPage, lastPage]
  );

  const pagesInChunkView = 5;
  const pagesForFullDisplay = 8;

  const buttonGroups: number[][] = useMemo(() => {
    if (lastPage < pagesForFullDisplay) {
      return [Array.from(Array(lastPage)).map((_, index) => index + 1)];
    }

    const isFirstChunkView = resolvedSelectedPage < pagesInChunkView;
    if (isFirstChunkView) {
      return [
        Array.from(Array(pagesInChunkView)).map((_, index) => index + 1),
        [lastPage],
      ];
    }
    const isLastChunckView =
      resolvedSelectedPage > lastPage + 1 - pagesInChunkView;
    if (isLastChunckView) {
      return [
        [1],
        Array.from(Array(pagesInChunkView))
          .map((_, index) => lastPage - index)
          .reverse(),
      ];
    }
    return [
      [1],
      [
        resolvedSelectedPage - 1,
        resolvedSelectedPage,
        resolvedSelectedPage + 1,
      ],
      [lastPage],
    ];
  }, [resolvedSelectedPage, lastPage]);

  const goToPage = useCallback(
    (page: number) => {
      if (page > 0 && page <= lastPage && page !== resolvedSelectedPage) {
        handlePageChange(page);
      }
    },
    [handlePageChange, resolvedSelectedPage, lastPage]
  );

  return (
    <PaginationView
      goToPage={goToPage}
      lastPage={lastPage}
      pageButtonGroups={buttonGroups}
      selectedPage={resolvedSelectedPage}
      theme={theme}
    />
  );
};

export const Pagination = memo(PaginationBase);
